import { useState, useEffect } from "react";

import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";

import {
  useMaterialUIController,
  setOpenConfigurator,
  setTransparentSidenav,
  setWhiteSidenav,
  setMiniSidenav,
  setFixedNavbar,
  setSidenavColor,
  setDarkMode,
} from "context";

function Configurator() {
  const [controller, dispatch] = useMaterialUIController();

  const {
    openConfigurator,
    miniSidenav,
    fixedNavbar,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [disabled, setDisabled] = useState(false);

  const sidenavColors = ["primary", "dark", "info", "success", "warning", "error"];

  useEffect(() => {
    function handleDisabled() {
      setDisabled(window.innerWidth <= 1200);
    }

    window.addEventListener("resize", handleDisabled);
    handleDisabled();

    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);

  const handleTransparentSidenav = () => {
    setTransparentSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
  };

  const handleWhiteSidenav = () => {
    setWhiteSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
  };

  const handleDarkSidenav = () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
  };

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleFixedNavbar = () => setFixedNavbar(dispatch, !fixedNavbar);
  const handleDarkMode = () => setDarkMode(dispatch, !darkMode);

  const cardStyle = ({ palette: { background, white }, boxShadows, borders }) => ({
    width: "100%",
    p: 2.5,
    borderRadius: "22px",
    backgroundColor: darkMode ? background.sidenav : white.main,
    boxShadow: boxShadows.lg,
    border: `${borders.borderWidth[1]} solid ${
        darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
    }`,
    overflow: "hidden",
  });

  const iconBoxStyle = (color) => ({
    width: 44,
    height: 44,
    minWidth: 44,
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
        color === "purple"
            ? "rgba(103, 58, 183, 0.12)"
            : color === "green"
                ? "rgba(0, 150, 136, 0.12)"
                : "rgba(63, 81, 181, 0.12)",
    color:
        color === "purple"
            ? "#673ab7"
            : color === "green"
                ? "#009688"
                : "#3f51b5",
  });

  const buttonBaseStyle = {
    minWidth: 0,
    height: 40,
    px: 1,
    borderRadius: "12px",
    fontSize: "12px",
    textTransform: "capitalize",
    whiteSpace: "nowrap",
  };

  const sidenavTypeButtonsStyles = ({
                                      palette: { white, dark, background },
                                      borders: { borderWidth },
                                    }) => ({
    ...buttonBaseStyle,
    background: darkMode ? background.sidenav : white.main,
    color: darkMode ? white.main : dark.main,
    border: `${borderWidth[1]} solid ${
        darkMode ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.14)"
    }`,
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",

    "&:hover": {
      background: darkMode ? background.sidenav : white.main,
    },
  });

  const sidenavTypeActiveButtonStyles = ({
                                           functions: { linearGradient },
                                           palette: { white, gradients, background },
                                         }) => ({
    ...buttonBaseStyle,
    background: darkMode
        ? white.main
        : linearGradient(gradients.dark.main, gradients.dark.state),
    color: darkMode ? background.sidenav : white.main,
    boxShadow: "0 8px 18px rgba(0,0,0,0.18)",

    "&:hover": {
      background: darkMode
          ? white.main
          : linearGradient(gradients.dark.main, gradients.dark.state),
    },
  });

  const settingItems = [
    {
      title: "Navbar Fixed",
      desc: "Keep navbar visible while scrolling",
      icon: "web_asset",
      color: "blue",
      checked: fixedNavbar,
      onChange: handleFixedNavbar,
    },
    {
      title: "Sidenav Mini",
      desc: "Collapse sidebar into compact mode",
      icon: "view_sidebar",
      color: "green",
      checked: miniSidenav,
      onChange: handleMiniSidenav,
    },
    {
      title: "Dark Mode",
      desc: "Switch between light and dark theme",
      icon: "dark_mode",
      color: "purple",
      checked: darkMode,
      onChange: handleDarkMode,
    },
  ];

  return (
      <ConfiguratorRoot
          variant="permanent"
          ownerState={{ openConfigurator }}
          sx={{
            width: { xs: 340, sm: 390, md: 430 },
            maxWidth: "100vw",
          }}
      >
        <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            pt={3}
            pb={2}
            px={3}
        >
          <MDBox pr={1}>
            <MDTypography variant="h6" fontWeight="bold">
              Dashboard Settings
            </MDTypography>

            <MDTypography
                variant="caption"
                color="text"
                sx={{ display: "block", mt: 0.4, lineHeight: 1.4 }}
            >
              Customize your workspace appearance
            </MDTypography>
          </MDBox>

          <IconButton
              size="small"
              onClick={handleCloseConfigurator}
              sx={({ palette: { dark, white } }) => ({
                width: 34,
                height: 34,
                borderRadius: "12px",
                color: darkMode ? white.main : dark.main,
                backgroundColor: darkMode
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.04)",
                transition: "all 0.2s ease",

                "&:hover": {
                  backgroundColor: darkMode
                      ? "rgba(255,255,255,0.14)"
                      : "rgba(0,0,0,0.08)",
                },
              })}
          >
            <Icon sx={{ fontSize: "18px !important" }}>close</Icon>
          </IconButton>
        </MDBox>

        <Divider />

        <MDBox px={3} py={3}>
          <MDBox sx={cardStyle}>
            <MDTypography variant="button" fontWeight="bold">
              Sidenav Colors
            </MDTypography>

            <MDTypography display="block" variant="caption" color="text" mt={0.5}>
              Select your preferred accent color
            </MDTypography>

            <MDBox display="flex" alignItems="center" gap={1.1} mt={2.2} flexWrap="wrap">
              {sidenavColors.map((color) => (
                  <Tooltip title={color} placement="top" key={color}>
                    <IconButton
                        sx={({
                               borders: { borderWidth },
                               palette: { white, dark, gradients },
                               functions: { linearGradient },
                             }) => ({
                          width: 31,
                          height: 31,
                          minWidth: 31,
                          padding: 0,
                          borderRadius: "50%",
                          backgroundImage: linearGradient(
                              gradients[color].main,
                              gradients[color].state
                          ),
                          border:
                              sidenavColor === color
                                  ? `${borderWidth[2]} solid ${
                                      darkMode ? white.main : dark.main
                                  }`
                                  : `${borderWidth[1]} solid transparent`,
                          transform: sidenavColor === color ? "scale(1.12)" : "scale(1)",
                          transition: "all 0.2s ease",
                          boxShadow:
                              sidenavColor === color
                                  ? "0 8px 18px rgba(0,0,0,0.20)"
                                  : "0 4px 10px rgba(0,0,0,0.10)",

                          "&:hover": {
                            transform: "scale(1.15)",
                          },
                        })}
                        onClick={() => setSidenavColor(dispatch, color)}
                    />
                  </Tooltip>
              ))}
            </MDBox>
          </MDBox>

          <MDBox mt={2.2} sx={cardStyle}>
            <MDTypography variant="button" fontWeight="bold">
              Sidenav Type
            </MDTypography>

            <MDTypography display="block" variant="caption" color="text" mt={0.5}>
              Choose the menu background style
            </MDTypography>

            <MDBox
                display="grid"
                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                gap={1.2}
                mt={2.2}
                sx={{
                  "@media (max-width: 360px)": {
                    gridTemplateColumns: "1fr",
                  },
                }}
            >
              <MDButton
                  color="dark"
                  variant="gradient"
                  onClick={handleDarkSidenav}
                  disabled={disabled}
                  fullWidth
                  sx={
                    !transparentSidenav && !whiteSidenav
                        ? sidenavTypeActiveButtonStyles
                        : sidenavTypeButtonsStyles
                  }
              >
                Dark
              </MDButton>

              <MDButton
                  color="dark"
                  variant="gradient"
                  onClick={handleTransparentSidenav}
                  disabled={disabled}
                  fullWidth
                  sx={
                    transparentSidenav && !whiteSidenav
                        ? sidenavTypeActiveButtonStyles
                        : sidenavTypeButtonsStyles
                  }
              >
                Transparent
              </MDButton>

              <MDButton
                  color="dark"
                  variant="gradient"
                  onClick={handleWhiteSidenav}
                  disabled={disabled}
                  fullWidth
                  sx={
                    whiteSidenav && !transparentSidenav
                        ? sidenavTypeActiveButtonStyles
                        : sidenavTypeButtonsStyles
                  }
              >
                White
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox mt={2.2} sx={cardStyle}>
            {settingItems.map((item, index) => (
                <MDBox key={item.title}>
                  <MDBox
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "44px minmax(0, 1fr) auto",
                        alignItems: "center",
                        columnGap: "14px",
                        py: 2,
                        width: "100%",
                      }}
                  >
                    <MDBox sx={iconBoxStyle(item.color)}>
                      <Icon fontSize="small">{item.icon}</Icon>
                    </MDBox>

                    <MDBox sx={{ minWidth: 0, overflow: "hidden" }}>
                      <MDTypography
                          variant="button"
                          fontWeight="bold"
                          sx={{
                            display: "block",
                            lineHeight: 1.2,
                          }}
                      >
                        {item.title}
                      </MDTypography>

                      <MDTypography
                          display="block"
                          variant="caption"
                          color="text"
                          sx={{
                            lineHeight: 1.4,
                            whiteSpace: "normal",
                            wordBreak: "normal",
                            pr: 1,
                          }}
                      >
                        {item.desc}
                      </MDTypography>
                    </MDBox>

                    <Switch checked={item.checked} onChange={item.onChange} />
                  </MDBox>

                  {index !== settingItems.length - 1 && <Divider />}
                </MDBox>
            ))}
          </MDBox>
        </MDBox>
      </ConfiguratorRoot>
  );
}

export default Configurator;